import { Component } from '@theme/component';

/**
 * Mobile burger menu for sections/heromkring-header.liquid (PRD A2).
 *
 * PLAN decision 5: no focus trap. Esc and a click outside close the panel,
 * and Esc returns focus to the burger. The element carries
 * data-skip-subtree-update, so a section morph (cart count, phase 3) leaves
 * the button's aria-expanded and the panel's hidden attribute alone.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The burger button.
 * @property {HTMLElement} panel - The menu panel (<nav hidden>).
 *
 * @extends {Component<Refs>}
 */
class HeromkringMenuToggle extends Component {
  requiredRefs = ['button', 'panel'];

  #desktop = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    this.#desktop.addEventListener('change', this.#onBreakpoint);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktop.removeEventListener('change', this.#onBreakpoint);
    this.#removeDocumentListeners();
  }

  get isOpen() {
    return !this.refs.panel.hidden;
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.refs.panel.hidden = false;
    this.refs.button.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  /**
   * @param {{ returnFocus?: boolean }} [options]
   */
  close({ returnFocus = false } = {}) {
    this.refs.panel.hidden = true;
    this.refs.button.setAttribute('aria-expanded', 'false');
    this.#removeDocumentListeners();
    if (returnFocus) this.refs.button.focus();
  }

  #removeDocumentListeners() {
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // Leave Esc to an open dialog (search modal, cart drawer).
    if (event.target instanceof Element && event.target.closest('dialog[open]')) return;
    this.close({ returnFocus: true });
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.close();
  };

  // The panel is hidden by CSS from 750px; close it so aria-expanded is
  // accurate if the viewport grows while it is open.
  #onBreakpoint = () => {
    if (this.#desktop.matches && this.isOpen) this.close();
  };
}

if (!customElements.get('heromkring-menu-toggle')) {
  customElements.define('heromkring-menu-toggle', HeromkringMenuToggle);
}
